import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

export const ACCORDION_SECTION_IDS = {
  GENERAL_SETTINGS: 'general-settings',
  OFFER_FIELDS: 'offer-fields',
  TAGS_AND_MARKETING: 'tags-and-marketing',
  OFFER_SETTINGS: 'offer-settings',
  PAYMENT_SCHEDULES: 'payment-schedules',
  DYNAMIC_PAYMENT_SCHEDULES: 'dynamic-payment-schedules',
  FLIGHTS: 'flights',
} as const;

type SectionId = (typeof ACCORDION_SECTION_IDS)[keyof typeof ACCORDION_SECTION_IDS];

interface AccordionExpandedContextValue {
  expandedSections: Set<SectionId>;
  toggleSection: (sectionId: SectionId) => void;
  isSectionExpanded: (sectionId: SectionId) => boolean;
}

const AccordionExpandedContext = createContext<AccordionExpandedContextValue | undefined>(undefined);

// Default expanded sections on initial load
const DEFAULT_EXPANDED_SECTIONS: Array<SectionId> = [
  ACCORDION_SECTION_IDS.GENERAL_SETTINGS,
  ACCORDION_SECTION_IDS.OFFER_FIELDS,
];

interface AccordionExpandedProviderProps {
  children: React.ReactNode;
}

export function AccordionExpandedProvider({ children }: AccordionExpandedProviderProps) {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Set<SectionId>>(() => new Set(DEFAULT_EXPANDED_SECTIONS));

  useEffect(() => {
    setExpandedSections(new Set(DEFAULT_EXPANDED_SECTIONS));
  }, [location.pathname]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && Object.values(ACCORDION_SECTION_IDS).includes(hash as SectionId)) {
      setExpandedSections((prev) => {
        const newSet = new Set(prev);
        newSet.add(hash as SectionId);
        return newSet;
      });
    }
  }, [location.pathname, location.hash]);

  const toggleSection = useCallback((sectionId: SectionId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  }, []);

  const isSectionExpanded = useCallback(
    (sectionId: SectionId) => {
      return expandedSections.has(sectionId);
    },
    [expandedSections],
  );

  const value: AccordionExpandedContextValue = {
    expandedSections,
    toggleSection,
    isSectionExpanded,
  };

  return <AccordionExpandedContext.Provider value={value}>{children}</AccordionExpandedContext.Provider>;
}

export function useAccordionExpanded() {
  const context = useContext(AccordionExpandedContext);
  if (context === undefined) {
    throw new Error('useAccordionExpanded must be used within an AccordionExpandedProvider');
  }
  return context;
}
